import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PhoneProfileAvatar from "../components/whatsapp/PhoneProfileAvatar";
import {
  getBusinessProfile,
  getPhoneHealth,
  syncPhoneHealth,
  updateBusinessProfile,
  uploadBusinessProfilePhoto,
} from "../apis/whatsappAccount";
import {
  formatMessagingLimit,
  formatNameStatus,
  formatQualityRating,
  nameStatusTone,
  qualityTone,
} from "../utils/phoneHealthLabels";

const VERTICAL_OPTIONS = [
  { value: "", label: "Not set" },
  { value: "OTHER", label: "Other" },
  { value: "AUTO", label: "Automotive" },
  { value: "BEAUTY", label: "Beauty, Spa & Salon" },
  { value: "APPAREL", label: "Clothing & Apparel" },
  { value: "EDU", label: "Education" },
  { value: "ENTERTAIN", label: "Entertainment" },
  { value: "EVENT_PLAN", label: "Event Planning" },
  { value: "FINANCE", label: "Finance & Banking" },
  { value: "GROCERY", label: "Food & Grocery" },
  { value: "GOVT", label: "Public Service" },
  { value: "HOTEL", label: "Hotel & Lodging" },
  { value: "HEALTH", label: "Medical & Health" },
  { value: "NONPROFIT", label: "Non-profit" },
  { value: "PROF_SERVICES", label: "Professional Services" },
  { value: "RETAIL", label: "Shopping & Retail" },
  { value: "TRAVEL", label: "Travel & Transportation" },
  { value: "RESTAURANT", label: "Restaurant" },
];

const toneClass = {
  good: "bg-emerald-100 text-emerald-800 border-emerald-200",
  warn: "bg-amber-100 text-amber-900 border-amber-200",
  bad: "bg-red-100 text-red-800 border-red-200",
  neutral: "bg-gray-100 text-gray-700 border-gray-200",
};

function StatusPill({ tone = "neutral", children }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${toneClass[tone] || toneClass.neutral}`}
    >
      {children}
    </span>
  );
}

function Section({ title, children, action }) {
  return (
    <section className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between gap-3">
        <h2 className="text-sm font-bold uppercase tracking-wider text-gray-700">
          {title}
        </h2>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

function ProfileField({ label, value, onEdit, editing, children }) {
  return (
    <div className="py-3 border-b border-gray-100 last:border-0">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
            {label}
          </p>
          {editing ? children : (
            <p className="text-sm text-gray-900 whitespace-pre-wrap break-words">
              {value || <span className="text-gray-400">Not set</span>}
            </p>
          )}
        </div>
        {onEdit && !editing && (
          <button
            type="button"
            onClick={onEdit}
            className="text-xs font-semibold text-gray-700 hover:text-gray-900 px-2.5 py-1 rounded-lg border border-gray-200 hover:bg-gray-50"
          >
            Edit
          </button>
        )}
      </div>
    </div>
  );
}

const WhatsappPhoneDetail = () => {
  const { phoneId } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [account, setAccount] = useState(null);
  const [profile, setProfile] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [history, setHistory] = useState([]);
  const [editKey, setEditKey] = useState(null);
  const [draft, setDraft] = useState({});
  const [nameHelpOpen, setNameHelpOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [healthRes, profileRes] = await Promise.all([
        getPhoneHealth(phoneId),
        getBusinessProfile(phoneId),
      ]);

      if (!healthRes.data?.success) {
        throw new Error(healthRes.data?.error || "Failed to load health");
      }
      setAccount(healthRes.data.data.account);
      setAlerts(healthRes.data.data.alerts || []);
      setHistory(healthRes.data.data.history || []);

      if (profileRes.data?.success) {
        setProfile(profileRes.data.data || {});
      } else {
        setProfile({});
      }
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || "Failed to load phone details"
      );
    } finally {
      setLoading(false);
    }
  }, [phoneId]);

  useEffect(() => {
    load();
  }, [load]);

  const startEdit = (key) => {
    setEditKey(key);
    if (key === "websites") {
      setDraft({
        websites: Array.isArray(profile?.websites)
          ? profile.websites.join("\n")
          : "",
      });
    } else {
      setDraft({ [key]: profile?.[key] || "" });
    }
  };

  const cancelEdit = () => {
    setEditKey(null);
    setDraft({});
  };

  const saveField = async () => {
    if (!editKey) return;
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const payload = {};
      if (editKey === "websites") {
        payload.websites = String(draft.websites || "")
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean)
          .slice(0, 2);
      } else {
        payload[editKey] = draft[editKey] ?? "";
      }
      const res = await updateBusinessProfile(phoneId, payload);
      if (!res.data?.success) {
        throw new Error(res.data?.error || "Update failed");
      }
      setNotice("Profile updated");
      cancelEdit();
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const onPhotoSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const res = await uploadBusinessProfilePhoto(phoneId, file);
      if (!res.data?.success) {
        throw new Error(res.data?.error || "Photo upload failed");
      }
      setNotice("Profile photo updated");
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Photo upload failed");
    } finally {
      setSaving(false);
    }
  };

  const onSync = async () => {
    setSyncing(true);
    setError("");
    setNotice("");
    try {
      const res = await syncPhoneHealth(phoneId);
      if (!res.data?.success) {
        throw new Error(res.data?.error || "Sync failed");
      }
      setNotice("Health synced from Meta");
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white p-6 flex items-center justify-center">
        <div className="animate-spin h-10 w-10 border-4 border-gray-300 border-t-gray-900 rounded-full" />
      </div>
    );
  }

  const displayNameStatus =
    account?.new_name_status &&
    account.new_name_status !== "NONE" &&
    account.new_name_status !== "APPROVED"
      ? account.new_name_status
      : account?.name_status;

  return (
    <div className="min-h-screen bg-[#f7f8f6] p-4 md:p-6">
      <div className="max-w-4xl mx-auto space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <button
              type="button"
              onClick={() => navigate("/waba")}
              className="text-sm text-gray-600 hover:text-gray-900 mb-2"
            >
              ← Phone Numbers
            </button>
            <h1 className="text-2xl font-bold text-gray-900">
              {account?.verified_name || "WhatsApp Number"}
            </h1>
            <p className="text-sm text-gray-600 mt-1">{account?.phone_number}</p>
          </div>
          <button
            type="button"
            onClick={onSync}
            disabled={syncing}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            <svg
              className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            {syncing ? "Syncing…" : "Sync from Meta"}
          </button>
        </div>

        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}
        {notice && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            {notice}
          </div>
        )}

        <Section title="Business Profile">
          <div className="flex items-center gap-4 mb-4">
            <PhoneProfileAvatar
              phoneId={phoneId}
              profilePictureUrl={profile?.profile_picture_url || account?.profile_picture_url}
              sizeClass="w-20 h-20"
            />
            <div>
              <p className="text-sm font-semibold text-gray-900 mb-2">Profile Photo</p>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onPhotoSelected}
              />
              <button
                type="button"
                disabled={saving}
                onClick={() => fileRef.current?.click()}
                className="text-xs font-semibold px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                {saving ? "Uploading…" : "Change Photo"}
              </button>
            </div>
          </div>

          <ProfileField
            label="Display Name"
            value={account?.verified_name}
            onEdit={() => setNameHelpOpen(true)}
          />
          {account?.requested_verified_name &&
            account?.new_name_status === "PENDING_REVIEW" && (
              <p className="text-xs text-amber-700 mb-2">
                Requested: {account.requested_verified_name} (under review)
              </p>
            )}
          {account?.name_rejection_reason && (
            <p className="text-xs text-red-700 mb-2">
              Rejection: {account.name_rejection_reason}
            </p>
          )}

          {[
            { key: "about", label: "About" },
            { key: "description", label: "Description" },
            { key: "address", label: "Address" },
            { key: "email", label: "Email" },
          ].map(({ key, label }) => (
            <ProfileField
              key={key}
              label={label}
              value={profile?.[key]}
              editing={editKey === key}
              onEdit={() => startEdit(key)}
            >
              <div className="space-y-2">
                <textarea
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm"
                  rows={key === "description" ? 4 : 2}
                  value={draft[key] || ""}
                  onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={saving}
                    onClick={saveField}
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-gray-900 text-white disabled:opacity-50"
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={cancelEdit}
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </ProfileField>
          ))}

          <ProfileField
            label="Business Category"
            value={
              VERTICAL_OPTIONS.find((v) => v.value === profile?.vertical)?.label ||
              profile?.vertical
            }
            editing={editKey === "vertical"}
            onEdit={() => startEdit("vertical")}
          >
            <div className="space-y-2">
              <select
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm"
                value={draft.vertical || ""}
                onChange={(e) => setDraft({ ...draft, vertical: e.target.value })}
              >
                {VERTICAL_OPTIONS.map((o) => (
                  <option key={o.value || "empty"} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={saving}
                  onClick={saveField}
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-gray-900 text-white disabled:opacity-50"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          </ProfileField>

          <ProfileField
            label="Websites"
            value={
              Array.isArray(profile?.websites) && profile.websites.length
                ? profile.websites.join("\n")
                : null
            }
            editing={editKey === "websites"}
            onEdit={() => startEdit("websites")}
          >
            <div className="space-y-2">
              <textarea
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm font-mono"
                rows={3}
                placeholder={"https://example.com\nhttps://instagram.com/..."}
                value={draft.websites || ""}
                onChange={(e) => setDraft({ ...draft, websites: e.target.value })}
              />
              <p className="text-xs text-gray-500">Up to 2 URLs, one per line</p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={saving}
                  onClick={saveField}
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-gray-900 text-white disabled:opacity-50"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          </ProfileField>
        </Section>

        <Section title="Account Health">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                Quality Rating
              </p>
              <StatusPill tone={qualityTone(account?.quality_rating)}>
                {formatQualityRating(account?.quality_rating)}
              </StatusPill>
            </div>
            <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                Messaging Limit
              </p>
              <p className="text-lg font-bold text-gray-900">
                {formatMessagingLimit(account?.messaging_limit)}
              </p>
            </div>
            <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                Number Status
              </p>
              <StatusPill
                tone={
                  String(account?.phone_number_status || "").toUpperCase() ===
                  "CONNECTED"
                    ? "good"
                    : account?.phone_number_status
                      ? "warn"
                      : "neutral"
                }
              >
                {account?.phone_number_status || "—"}
              </StatusPill>
            </div>
            <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                Display Name Status
              </p>
              <StatusPill tone={nameStatusTone(displayNameStatus)}>
                {formatNameStatus(displayNameStatus)}
              </StatusPill>
            </div>
          </div>
        </Section>

        <Section title="Alerts">
          {alerts.length === 0 ? (
            <p className="text-sm text-gray-500">No active alerts</p>
          ) : (
            <ul className="space-y-3">
              {alerts.map((a) => (
                <li
                  key={a.id}
                  className={`rounded-xl border px-4 py-3 text-sm ${
                    a.severity === "critical"
                      ? "border-red-200 bg-red-50 text-red-900"
                      : "border-amber-200 bg-amber-50 text-amber-900"
                  }`}
                >
                  <p className="font-semibold">{a.title}</p>
                  <p className="text-xs opacity-80 mt-1">
                    {new Date(a.created_at).toLocaleString()}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title="History">
          {history.length === 0 ? (
            <p className="text-sm text-gray-500">No events yet — try Sync from Meta</p>
          ) : (
            <ul className="space-y-2">
              {history.map((h) => (
                <li
                  key={h.id}
                  className="flex items-start justify-between gap-3 py-2 border-b border-gray-100 last:border-0"
                >
                  <div>
                    <p className="text-sm text-gray-900">{h.title}</p>
                    <p className="text-xs text-gray-500">{h.event_type}</p>
                  </div>
                  <p className="text-xs text-gray-500 whitespace-nowrap">
                    {new Date(h.created_at).toLocaleDateString()}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>

      {nameHelpOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl">
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              Request Display Name Change
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              WhatsApp display names go through Meta review. Submit the change in
              WhatsApp Manager, then we&apos;ll update status here via the{" "}
              <code className="text-xs bg-gray-100 px-1 rounded">
                phone_number_name_update
              </code>{" "}
              webhook (Approved / Rejected).
            </p>
            <ol className="text-sm text-gray-700 list-decimal pl-5 space-y-1 mb-5">
              <li>Open Meta WhatsApp Manager</li>
              <li>Select this phone number → Profile</li>
              <li>Edit Display Name and submit for review</li>
              <li>Return here and click Sync if needed</li>
            </ol>
            <div className="flex gap-2 justify-end">
              <a
                href="https://business.facebook.com/wa/manage/phone-numbers/"
                target="_blank"
                rel="noreferrer"
                className="text-sm font-semibold px-4 py-2 rounded-xl bg-gray-900 text-white"
              >
                Open WhatsApp Manager
              </a>
              <button
                type="button"
                onClick={() => setNameHelpOpen(false)}
                className="text-sm font-semibold px-4 py-2 rounded-xl border border-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsappPhoneDetail;
